import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Card } from '../../ui/Card';
import { FiUsers, FiGlobe, FiArrowUp, FiRotateCw, FiArrowDown, FiShoppingBag, FiShield, FiUserCheck, FiUser, FiTrendingUp } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { loadAllGuardsForExport, GuardData } from '../../../pages/manage_guard/service/guardService';
import { fetchFactoryClients } from '../../../pages/manage_clients/factory_clients/service/factoryClientService';
import { fetchIndividualClients } from '../../../pages/manage_clients/individual_clients/service/individualClientService';
import { fetchBankClients } from '../../../pages/manage_clients/bank_clients/service/bankClientService';
import { StaffData } from '../../../firebase/auth';

const StatsCardWrapper = styled.div`
  width: 100%;
  margin-bottom: ${theme.spacing[6]};
  
  @media (min-width: ${theme.breakpoints.xl}) {
    max-width: 50%;
  }
`;

const StatsCard = styled(Card)`
  width: 100%;
`;

const RowTable = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-bottom: 1px solid ${theme.colors.light.border.light};
  
  &:last-child {
    border-bottom: none;
  }
`;

const StatItem = styled.div`
  flex: 1;
  min-width: 33.333%;
  padding: ${theme.spacing[6]};
  text-align: center;
  border-right: 1px solid ${theme.colors.light.border.light};
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: ${theme.breakpoints.md}) {
    min-width: 50%;
    border-bottom: 1px solid ${theme.colors.light.border.light};
    
    &:nth-child(3) {
      border-right: none;
    }
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    min-width: 100%;
    border-right: none;
  }
`;

const StatIcon = styled.div<{ color: string }>`
  color: ${({ color }) => color};
  font-size: 24px;
  margin-bottom: ${theme.spacing[2]};
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const StatValue = styled.div`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

interface Metric {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
}

const MetricCards: React.FC = () => {
  const { companyData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<Metric[]>([]);

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!companyData?.id) {
        setLoading(false);
        return;
      }

      try {
        const companyId = companyData.id;

        // Fetch all data in parallel
        const [guards, factoryClients, individualClients, bankClients, staffSnapshot] = await Promise.all([
          loadAllGuardsForExport(companyId),
          fetchFactoryClients(companyId).catch(() => []),
          fetchIndividualClients(companyId).catch(() => []),
          fetchBankClients(companyId).catch(() => []),
          getDocs(collection(db, 'companies', companyId, 'staff')),
        ]);

        // Calculate guard metrics
        const totalGuards = guards.length;
        const activeGuards = guards.filter((g) => g.isActive !== false).length;
        const inactiveGuards = totalGuards - activeGuards;
        const deployedGuards = guards.filter((g) => g.currentDeployment).length;
        const undeployedGuards = totalGuards - deployedGuards;
        
        // Calculate total salary
        const totalSalary = guards.reduce((sum, guard) => {
          const salary = typeof guard.salary === 'number' ? guard.salary : parseFloat(String(guard.salary || 0));
          return sum + (isNaN(salary) ? 0 : salary);
        }, 0);

        // Calculate client metrics
        const totalFactoryClients = factoryClients.length;
        const totalIndividualClients = individualClients.length;
        const totalBankBranches = bankClients.length;
        const totalClients = totalFactoryClients + totalIndividualClients + totalBankBranches;
        const uniqueBanks = new Set(bankClients.map((b) => b.bankName)).size;

        // Calculate staff metrics
        const staffList: StaffData[] = [];
        staffSnapshot.forEach((doc) => {
          staffList.push(doc.data() as StaffData);
        });
        const totalStaff = staffList.length;
        const activeStaff = staffList.filter((s) => s.isActive !== false).length;
        const inactiveStaff = totalStaff - activeStaff;

        // Calculate deployment metrics
        const totalDeployments = deployedGuards;
        const temporaryDeployments = guards.filter((g) => {
          const deployment = g.currentDeployment;
          return deployment && deployment.status === 'temporary';
        }).length;
        const permanentDeployments = deployedGuards - temporaryDeployments;

        // Calculate monthly metrics (current month)
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        const newHiringThisMonth = guards.filter((g) => {
          if (!g.dateOfJoining) return false;
          const joinDate = g.dateOfJoining?.toDate ? g.dateOfJoining.toDate() : new Date(g.dateOfJoining);
          return joinDate.getMonth() === currentMonth && joinDate.getFullYear() === currentYear;
        }).length;

        const newClientsThisMonth = [
          ...factoryClients.filter((c) => {
            if (!c.dateOfRegistration) return false;
            const regDate = c.dateOfRegistration?.toDate ? c.dateOfRegistration.toDate() : new Date(c.dateOfRegistration);
            return regDate.getMonth() === currentMonth && regDate.getFullYear() === currentYear;
          }),
          ...individualClients.filter((c) => {
            if (!c.dateOfRegistration) return false;
            const regDate = c.dateOfRegistration?.toDate ? c.dateOfRegistration.toDate() : new Date(c.dateOfRegistration);
            return regDate.getMonth() === currentMonth && regDate.getFullYear() === currentYear;
          }),
          ...bankClients.filter((c) => {
            if (!c.dateOfRegistration) return false;
            const regDate = c.dateOfRegistration?.toDate ? c.dateOfRegistration.toDate() : new Date(c.dateOfRegistration);
            return regDate.getMonth() === currentMonth && regDate.getFullYear() === currentYear;
          }),
        ].length;

        // Set metrics
        setMetrics([
          {
            icon: <FiShield />,
            label: 'Total Guards',
            value: totalGuards.toString(),
            color: theme.colors.primary[500],
          },
          {
            icon: <FiUsers />,
            label: 'Total Staff',
            value: totalStaff.toString(),
            color: theme.colors.primary[600],
          },
          {
            icon: <FiGlobe />,
            label: 'Total Clients',
            value: totalClients.toString(),
            color: theme.colors.primary[500],
          },
          {
            icon: <FiArrowUp />,
            label: 'Total Branches',
            value: totalBankBranches.toString(),
            color: theme.colors.primary[600],
          },
          {
            icon: <FiRotateCw />,
            label: 'Total Deployment',
            value: totalDeployments.toString(),
            color: theme.colors.primary[500],
          },
          {
            icon: <FiTrendingUp />,
            label: 'Total Salary',
            value: `Rs. ${(totalSalary / 1000).toFixed(0)}K`,
            color: theme.colors.primary[600],
          },
        ]);
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [companyData]);

  if (loading) {
    return (
      <StatsCardWrapper>
        <StatsCard>
        <RowTable>
          {[1, 2, 3].map((i) => (
            <StatItem key={i}>
              <StatIcon color={theme.colors.primary[500]}>
                <FiUsers />
              </StatIcon>
              <StatValue>...</StatValue>
              <StatLabel>Loading...</StatLabel>
            </StatItem>
          ))}
        </RowTable>
        <RowTable>
          {[4, 5, 6].map((i) => (
            <StatItem key={i}>
              <StatIcon color={theme.colors.primary[500]}>
                <FiUsers />
              </StatIcon>
              <StatValue>...</StatValue>
              <StatLabel>Loading...</StatLabel>
            </StatItem>
          ))}
        </RowTable>
        </StatsCard>
      </StatsCardWrapper>
    );
  }

  return (
    <StatsCardWrapper>
      <StatsCard>
      <RowTable>
        {metrics.slice(0, 3).map((metric, index) => (
          <StatItem key={index}>
            <StatIcon color={metric.color}>{metric.icon}</StatIcon>
            <StatValue>{metric.value}</StatValue>
            <StatLabel>{metric.label}</StatLabel>
          </StatItem>
        ))}
      </RowTable>
      <RowTable>
        {metrics.slice(3, 6).map((metric, index) => (
          <StatItem key={index + 3}>
            <StatIcon color={metric.color}>{metric.icon}</StatIcon>
            <StatValue>{metric.value}</StatValue>
            <StatLabel>{metric.label}</StatLabel>
          </StatItem>
        ))}
      </RowTable>
      </StatsCard>
    </StatsCardWrapper>
  );
};

export default MetricCards;

